import { useState } from 'react';
import { IncompleteParamsSchema } from '../schemas/parameters.schema';
import type { IncompleteParam, ReferenceUpdate, StateReferenceDTO } from '../types/parameters.types';
import { toFieldErrors, type FieldErrors } from '../utils/formErrors';

interface UseIncompleteParamsFormParams {
	reference: StateReferenceDTO;
	params: IncompleteParam[];
	onSubmit: (update: ReferenceUpdate) => Promise<boolean>;
	onClose: () => void;
}

type EditableField = 'method' | 'unit' | 'idealRange';

const buildUpdate = (reference: StateReferenceDTO, rows: IncompleteParam[]): ReferenceUpdate => {
	const fixedRows = rows.filter((row) => row.key === 'fixedParams');
	const dependentRows = rows.filter((row) => row.key === 'dependentParams');
	return {
		...(fixedRows.length > 0 && {
			fixedParams: reference.fixedParams.map((param, index) => {
				const row = fixedRows.find((item) => item.index === index);
				return row
					? {
							...param,
							method: row.method.trim(),
							unit: row.unit.trim(),
							idealRange: (row.idealRange ?? '').trim(),
						}
					: param;
			}),
		}),
		...(dependentRows.length > 0 && {
			dependentParams: reference.dependentParams.map((param, index) => {
				const row = dependentRows.find((item) => item.index === index);
				return row ? { ...param, method: row.method.trim(), unit: row.unit.trim() } : param;
			}),
		}),
	};
};

export const useIncompleteParamsForm = ({ reference, params, onSubmit, onClose }: UseIncompleteParamsFormParams) => {
	const [rows, setRows] = useState(params);
	const [errors, setErrors] = useState<FieldErrors>({});

	const setField = (index: number, field: EditableField, value: string) => {
		setErrors((previous) => ({ ...previous, [`${index}.${field}`]: '' }));
		setRows((previous) => previous.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
	};

	const submit = async () => {
		const parsed = IncompleteParamsSchema.safeParse(rows);
		if (!parsed.success) {
			setErrors(toFieldErrors(parsed.error));
			return;
		}
		if (await onSubmit(buildUpdate(reference, rows))) onClose();
	};

	return { rows, errors, setField, submit };
};
